import type { ViewJobReadableCookies } from '../../../../../shared/native-modules/core/getCookies';

import { PROMO_CONSTANTS, RELEVANT_TEST_NAMES } from './constants';
import {
    enableG1T,
    incrementPageVisitCountCookie,
    suppressG1T,
    incrementSessionCountCookie,
    incrementMonthOrWeeklyCountCookie,
    shouldShowWebToAppPromo,
    incrementClosedCountInSessionCookie,
    incrementClosedCountInMonthCookie,
} from './cookies';
import { BrowserIcon, IndeedIcon } from './WebToAppPromoIcons';
import { Box, Button, Text, Heading, TextLink, Modal } from '../../../../../mads';
import { logAppPromoInteraction } from '../../../../../shared/app-promo/logAppPromoInteraction';
import { getOnelinkUrlInner } from '../../../../../shared/app-promo/oneLink';
import { getWebToAppPromoProctorGroups } from '../../../../../shared/app-promo/proctorUtils';
import { useI18n } from '../../../../../shared/I18n';
import { CookieKey } from '../../../../../shared/native-modules/types/RNCoreNativeModule';
import { useCookies } from '../../../../../shared/util/CookieProvider';
import { useViewjobProctorAllocation } from '../../../../proctor/__generated__/ViewjobProctorContext';
import { JsjRnMobVjOffsiteW2aPromoTst } from '../../../../proctor/__generated__/ViewjobProctorSpecifications';
import {
    useViewJobDataContext,
    useViewjobOneGraphModel,
} from '../../../context/ViewJobDataContext';

import { colors, spacing, borderRadius } from '@indeed/ifl-tokens/react-native';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';

interface WebToAppPromoProps {
    onClose?: () => void;
}

const styles = StyleSheet.create({
    contentContainer: {
        width: '100%',
    },
    textSpacing: {
        paddingBottom: spacing.s2,
    },
    iconContainer: {
        height: 48,
        width: 48,
        borderRadius: borderRadius.md,
        borderWidth: 1,
        borderColor: colors.borderWeak,
        alignItems: 'center' as const,
        justifyContent: 'center' as const,
    },
    appRow: {
        flexDirection: 'row' as const,
        alignItems: 'center' as const,
        gap: spacing.s3,
        marginTop: spacing.s4,
    },
    appText: {
        flex: 1,
    },
    openButton: {
        minWidth: 120,
    },
    employerLinkContainer: {
        flexDirection: 'row' as const,
        justifyContent: 'center' as const,
        marginTop: spacing.s4,
    },
    modalOverlayRoot: {
        flex: 1,
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
    modalOverlay: {
        backgroundColor: colors.utilityOverlay,
        position: 'absolute',
        inset: 0,
    },
    modalContentWrapper: {
        width: '100%',
        maxWidth: 736,
        backgroundColor: colors.background,
        borderTopLeftRadius: borderRadius.lg,
        borderTopRightRadius: borderRadius.lg,
        flexDirection: 'column' as const,
        overflow: 'hidden' as const,
    },
    modalScrollableContent: {
        padding: spacing.s4,
        paddingBottom: spacing.s5,
    },
});

/**
 * Shows an app download promo as a modal
 */
export function WebToAppPromo({ onClose }: WebToAppPromoProps) {
    const { gettext } = useI18n();
    const viewjobOneGraphModel = useViewjobOneGraphModel();
    const viewJobDataContext = useViewJobDataContext();
    const cookies = useCookies<ViewJobReadableCookies>();

    const ctk = viewjobOneGraphModel?.ctk;
    const mobtk = viewjobOneGraphModel?.mobvjtk;
    const isLoggedIn = viewjobOneGraphModel?.jobSeeker?.loggedIn;
    const country = viewJobDataContext.initialData?.country;
    const language = viewJobDataContext.initialData?.language;
    const proctorGroupsString = viewJobDataContext.proctorGroups;
    const useIsAutoClickApply = !!(
        viewJobDataContext.initialData?.autoClickApply ||
        viewJobDataContext.initialData?.autoClickApplyNonIa
    );

    const proctorAllocation = useViewjobProctorAllocation(JsjRnMobVjOffsiteW2aPromoTst);

    const filteredProctorGroups = useMemo(
        () => getWebToAppPromoProctorGroups(proctorGroupsString, RELEVANT_TEST_NAMES),
        [proctorGroupsString],
    );

    // Modal is never rendered during SSR or hydration
    const [modalVisible, setModalVisible] = useState(false);

    // Evaluate the actual state of the modal after hydration.
    // Suppress for GFJ auto-click-apply traffic so the modal doesn't block the apply flow.
    useEffect(() => {
        setModalVisible(!useIsAutoClickApply && shouldShowWebToAppPromo(cookies, country));
    }, [cookies, country, useIsAutoClickApply]);

    const hasLoggedImpression = useRef(false);
    const originalOverflow = useRef<string>('');

    const sessionClosedCount = Number(cookies[CookieKey.maguaPromoClosedInSessionCount]) || 0;
    const monthClosedCount = Number(cookies[CookieKey.maguaPromoClosedInMonthCount]) || 0;

    const handleLogInteraction = useCallback(
        (interactionType: 'IMPRESSION' | 'CLICK' | 'DISMISS' | 'EMP_CLICK') => {
            logAppPromoInteraction(
                { ctk, mobtk, isLoggedIn, country, language },
                {
                    hostId: PROMO_CONSTANTS.VJ_HOST_ID,
                    pageId: PROMO_CONSTANTS.VJ_PAGE_ID,
                    campaign: PROMO_CONSTANTS.VJ_CAMPAIGN,
                    experiments: filteredProctorGroups,
                    promoInstanceInSession: sessionClosedCount + 1,
                    promoInstanceInMonth: monthClosedCount + 1,
                },
                interactionType,
            );
        },
        [
            ctk,
            mobtk,
            isLoggedIn,
            country,
            language,
            filteredProctorGroups,
            sessionClosedCount,
            monthClosedCount,
        ],
    );

    useEffect(() => {
        if (!proctorAllocation.isActive || useIsAutoClickApply) {
            return;
        }
        enableG1T(cookies, country);
        incrementPageVisitCountCookie(cookies);
    }, [cookies, country, proctorAllocation.isActive, useIsAutoClickApply]);

    useEffect(() => {
        if (proctorAllocation.isActive && modalVisible && !hasLoggedImpression.current) {
            hasLoggedImpression.current = true;
            suppressG1T();
            incrementSessionCountCookie(cookies);
            incrementMonthOrWeeklyCountCookie(cookies);
            handleLogInteraction('IMPRESSION');
        }
    }, [cookies, modalVisible, handleLogInteraction, proctorAllocation.isActive]);

    // Manage body overflow to prevent page scrolling when modal is open
    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (!proctorAllocation.isActive) return;

        if (modalVisible) {
            originalOverflow.current = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        } else if (originalOverflow.current !== undefined) {
            document.body.style.overflow = originalOverflow.current;
        }

        // Cleanup: restore overflow on unmount
        return () => {
            if (originalOverflow.current !== undefined) {
                document.body.style.overflow = originalOverflow.current;
            }
        };
    }, [modalVisible, proctorAllocation.isActive]);

    const handleCloseModal = useCallback(() => {
        handleLogInteraction('DISMISS');
        incrementClosedCountInSessionCookie(cookies);
        incrementClosedCountInMonthCookie(cookies);
        setModalVisible(false);
        hasLoggedImpression.current = false;
        onClose?.();
    }, [cookies, handleLogInteraction, onClose]);

    const handleOpenApp = useCallback(() => {
        handleLogInteraction('CLICK');
    }, [handleLogInteraction]);

    const oneLinkUrl = useMemo(() => {
        return getOnelinkUrlInner(
            PROMO_CONSTANTS.VJ_CAMPAIGN,
            filteredProctorGroups,
            isLoggedIn || false,
            {
                promoInstanceInSession: sessionClosedCount + 1,
                promoInstanceInMonth: monthClosedCount + 1,
            },
        );
    }, [filteredProctorGroups, isLoggedIn, sessionClosedCount, monthClosedCount]);

    const handleEmployerCTA = useCallback(() => {
        handleLogInteraction('EMP_CLICK');
    }, [handleLogInteraction]);

    if (!proctorAllocation.isActive || useIsAutoClickApply) {
        return null;
    }

    if (!modalVisible) {
        return null;
    }

    return (
        <Modal
            visible={modalVisible}
            transparent={true}
            animationType="none"
            onRequestClose={handleCloseModal}
        >
            <Box sx={styles.modalOverlayRoot}>
                <Box sx={styles.modalOverlay} />
                <Box sx={styles.modalContentWrapper}>
                    <Box sx={styles.modalScrollableContent}>
                        <Box sx={styles.contentContainer}>
                            <Box sx={styles.textSpacing}>
                                <Text level={1}>{gettext('Source: Indeed data (US)')}</Text>
                            </Box>

                            <Box sx={styles.textSpacing}>
                                <Heading level={4}>
                                    {gettext('Mobile app users get hired 30% faster on average')}
                                </Heading>
                            </Box>

                            <Box sx={styles.appRow}>
                                <Box sx={styles.iconContainer}>
                                    <IndeedIcon />
                                </Box>
                                <Box sx={styles.appText}>
                                    <Text level={3}>{gettext('Indeed Job Search')}</Text>
                                </Box>
                                <Box sx={styles.openButton}>
                                    <Button
                                        href={oneLinkUrl}
                                        onPress={handleOpenApp}
                                        variant="primary"
                                        size="lg"
                                        ariaLabel={gettext('Open Indeed Job Search App')}
                                    >
                                        {gettext('Open')}
                                    </Button>
                                </Box>
                            </Box>

                            <Box sx={styles.appRow}>
                                <Box sx={styles.iconContainer}>
                                    <BrowserIcon />
                                </Box>
                                <Box sx={styles.appText}>
                                    <Text level={3}>{gettext('Browser')}</Text>
                                </Box>
                                <Box sx={styles.openButton}>
                                    <Button
                                        onPress={handleCloseModal}
                                        variant="secondary"
                                        size="lg"
                                        ariaLabel={gettext('Continue With Browser')}
                                    >
                                        {gettext('Continue')}
                                    </Button>
                                </Box>
                            </Box>

                            <Box sx={styles.employerLinkContainer}>
                                <TextLink
                                    targetUrl={PROMO_CONSTANTS.EMPLOYER_CTA_URL}
                                    onPress={handleEmployerCTA}
                                >
                                    {gettext('Hiring? Visit Indeed for employers')}
                                </TextLink>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
}
